import React, { useEffect, useState, Suspense, useCallback } from "react";
import { redirectToSpotifyLogin, exchangeCodeForToken, getValidToken } from "./Spotify";
import "./App.css";

const Auth    = React.lazy(() => import("./components/Auth"));
const Search  = React.lazy(() => import("./components/Search"));
const Save    = React.lazy(() => import("./components/Save"));
const Profile = React.lazy(() => import("./components/Profile"));
const Manage  = React.lazy(() => import("./components/Manage"));

const LoadingSpinner = () => (
  <div className="loading-spinner"><div className="spinner"></div></div>
);

class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, errorInfo) { console.error("Error:", error, errorInfo); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <h1>Something went wrong</h1>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const STEPS = { SEARCH: "search", SAVE: "save", PROFILE: "profile", MANAGE: "manage" };

function App() {
  const [token, setToken] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [currentStep, setCurrentStep] = useState(
    () => localStorage.getItem("currentStep") || STEPS.SEARCH
  );
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    [
      "spotify_token",
      "spotify_refresh_token",
      "spotify_token_expires_at",
      "pkce_verifier",
      "currentStep",
      "selectedTracks",
    ].forEach(k => localStorage.removeItem(k));
    setToken(null);
    setCurrentStep(STEPS.SEARCH);
    setSelectedTracks([]);
    setShowAuth(false);
  }, []);

  useEffect(() => {
    const handleAuth = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const urlError = params.get("error");
        if (urlError) {
          window.history.replaceState({}, "", window.location.pathname);
          setShowAuth(true);
          setLoading(false);
          return;
        }

        if (code) {
          window.history.replaceState({}, "", window.location.pathname);
          [
            "spotify_token",
            "spotify_refresh_token",
            "spotify_token_expires_at",
            "currentStep",
            "selectedTracks",
          ].forEach(k => localStorage.removeItem(k));
          const tokenData = await exchangeCodeForToken(code);
          const testRes = await fetch("https://api.spotify.com/v1/me", {
            headers: { Authorization: `Bearer ${tokenData.access_token}` }
          });
          if (testRes.status === 403) {
            [
              "spotify_token", "spotify_refresh_token",
              "spotify_token_expires_at", "currentStep", "selectedTracks",
            ].forEach(k => localStorage.removeItem(k));
            setAccessDenied(true);
            setLoading(false);
            return;
          }
          setToken(tokenData.access_token);
          setCurrentStep(STEPS.SEARCH);
          setSelectedTracks([]);
        } else {
          const storedToken = localStorage.getItem("spotify_token");
          if (storedToken) {
            try {
              const validToken = await getValidToken();
              const testRes = await fetch("https://api.spotify.com/v1/me", {
                headers: { Authorization: `Bearer ${validToken}` }
              });
              if (testRes.status === 403) {
                clearSession();
                setAccessDenied(true);
                return;
              }
              setToken(validToken);
              const savedStep = localStorage.getItem("currentStep");
              if (savedStep) setCurrentStep(savedStep);
              const savedTracks = localStorage.getItem("selectedTracks");
              if (savedTracks) setSelectedTracks(JSON.parse(savedTracks));
            } catch { clearSession(); }
          }
        }
      } catch (error) {
        console.error("Auth error:", error);
        clearSession();
      } finally {
        setLoading(false);
      }
    };
    handleAuth();
  }, [clearSession]);

  useEffect(() => {
    if (currentStep) localStorage.setItem("currentStep", currentStep);
  }, [currentStep]);

  useEffect(() => {
    if (selectedTracks.length > 0)
      localStorage.setItem("selectedTracks", JSON.stringify(selectedTracks));
  }, [selectedTracks]);

  const handleTokenRefresh = useCallback(async () => {
    try {
      const validToken = await getValidToken();
      setToken(validToken);
      return validToken;
    } catch {
      clearSession();
      return null;
    }
  }, [clearSession]);

  const handleTrackSelection = (track) => {
    setSelectedTracks(prev => {
      if (track.isRemoving) return prev.filter(t => t.id !== track.id);
      if (prev.some(t => t.id === track.id)) return prev;
      return [...prev, track];
    });
  };

  if (loading) return <LoadingSpinner />;

  if (accessDenied) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Auth accessDenied onLogin={redirectToSpotifyLogin} onBack={() => { setAccessDenied(false); setShowAuth(false); }} />
      </Suspense>
    );
  }

  if (showAuth) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Auth onLogin={redirectToSpotifyLogin} onBack={() => setShowAuth(false)} />
      </Suspense>
    );
  }

  return (
    <ErrorBoundary>
      <div className={`App ${token ? "authenticated" : ""}`}>
        <header className="App-header">
          {!token ? (
            <div className="login-container">
              <div className="App-logo-container">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/logo.png`}
                  alt="JamLite Logo"
                  className="App-logo"
                />
              </div>
              <div className="title-container">
                <h2 className="Sub-title">Welcome to</h2>
                <h1 className="App-title">JamLite</h1>
              </div>
              <button className="App-button" onClick={() => setShowAuth(true)}>
                Login with Spotify
              </button>
            </div>
          ) : (
            <Suspense fallback={<LoadingSpinner />}>
              <nav className="nav-buttons">
                <div className="nav-left">
                  <a
                    href="https://andresnalegre.github.io/JamLite/"
                    className="nav-brand"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/logo.png`}
                      alt="JamLite"
                      className="nav-logo"
                    />
                    <span className="nav-brand-name">JamLite</span>
                  </a>
                  {currentStep === STEPS.SAVE && (
                    <button className="back-button" onClick={() => setCurrentStep(STEPS.SEARCH)}>
                      Back
                    </button>
                  )}
                </div>
                <div className="nav-center">
                  <button
                    className={`nav-tab ${[STEPS.SEARCH, STEPS.SAVE].includes(currentStep) ? "active" : ""}`}
                    onClick={() => setCurrentStep(STEPS.SEARCH)}
                  >
                    Search
                  </button>
                  <button
                    className={`nav-tab ${currentStep === STEPS.MANAGE ? "active" : ""}`}
                    onClick={() => setCurrentStep(STEPS.MANAGE)}
                  >
                    Manage
                  </button>
                  <button
                    className={`nav-tab ${currentStep === STEPS.PROFILE ? "active" : ""}`}
                    onClick={() => setCurrentStep(STEPS.PROFILE)}
                  >
                    Profile
                  </button>
                </div>
                <div className="nav-right">
                  <button className="logout-button" onClick={clearSession}>Logout</button>
                </div>
              </nav>

              {currentStep === STEPS.SEARCH && (
                <Search
                  token={token}
                  onTokenRefresh={handleTokenRefresh}
                  onSearchResultsUpdate={handleTrackSelection}
                  onNext={() => setCurrentStep(STEPS.SAVE)}
                />
              )}
              {currentStep === STEPS.SAVE && (
                <Save
                  token={token}
                  onTokenRefresh={handleTokenRefresh}
                  selectedTracks={selectedTracks}
                  onNewPlaylist={() => {
                    setSelectedTracks([]);
                    localStorage.removeItem("selectedTracks");
                    setCurrentStep(STEPS.SEARCH);
                  }}
                  onBack={() => setCurrentStep(STEPS.SEARCH)}
                />
              )}
              {currentStep === STEPS.PROFILE && (
                <Profile token={token} onTokenRefresh={handleTokenRefresh} />
              )}
              {currentStep === STEPS.MANAGE && (
                <Manage token={token} onTokenRefresh={handleTokenRefresh} />
              )}
            </Suspense>
          )}
        </header>
      {token && (
        <footer className="app-footer">
          <span>© 2026 JamLite · All rights reserved</span>
        </footer>
      )}
      </div>
    </ErrorBoundary>
  );
}

export default App;